import { Button } from '@design/components/button/Button';
import { ButtonLink } from '@design/components/button-link/ButtonLink';
import { Drawer } from '@design/components/drawer/Drawer';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useChatSettings } from '@terminal/views/alfred/research/use-chat-settings/useChatSettings';
import { MarkdownEditor } from '@terminal/widgets/markdown-editor/MarkdownEditor';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './PersonalizationDrawer.module.scss';

export const PersonalizationDrawer = () => {
  const { t } = useTranslation('chat');
  const { trackAlfredPersonalize } = usePosthogEvents();

  const {
    setPreferenceFormat,
    preferenceFormat,
    displayPersonalizationDrawer,
    setDisplayPersonalizationDrawer,
  } = useChatSettings(state => ({
    displayPersonalizationDrawer: state.displayPersonalizationDrawer,
    setDisplayPersonalizationDrawer: state.setDisplayPersonalizationDrawer,
    preferenceFormat: state.preferenceFormat,
    setPreferenceFormat: state.setPreferenceFormat,
  }));
  const [instructions, setInstructions] = useState(preferenceFormat || '');
  const [isLoading, setIsLoading] = useState(false);

  const closeDrawer = () => {
    setDisplayPersonalizationDrawer(false);
  };

  const handleSave = async () => {
    setIsLoading(true);
    const response = await setPreferenceFormat(instructions);

    if (!response.error) {
      trackAlfredPersonalize(POSTHOG_TRACKING_SOURCES.ALFRED);
    }

    setIsLoading(false);
  };

  const handleCancel = () => {
    setInstructions('');
    closeDrawer();
  };

  const showExample = () => {
    setInstructions(
      state => state + '\n' + t('settings.exampleText', { joinArrays: '\n' })
    );
  };

  useEffect(() => {
    if (preferenceFormat) {
      setInstructions(preferenceFormat);
    }
  }, [preferenceFormat]);

  return (
    <Drawer
      isOpen={displayPersonalizationDrawer}
      handleCloseClick={closeDrawer}
      data-testid="personalization-drawer"
    >
      <div className={styles.container}>
        <h2 className={styles.title}>{t('settings.title')}</h2>
        <div className={styles.content}>
          <div className={styles.description}>{t('settings.subHeader')}</div>
          <div className={styles.inputWrapper}>
            <div className={styles.labels}>
              <span>{t('settings.instructions')}</span>
              <ButtonLink
                label={t('settings.showExample')}
                onClick={showExample}
              />
            </div>
            <div className={styles.editorWrapper}>
              <MarkdownEditor
                value={instructions}
                onChange={setInstructions}
                placeholder={t('settings.placeholder')}
                className="personalization-editor"
              />
            </div>

            <div className={styles.proTip}>{t('settings.proTip')}</div>
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="tertiary"
            size="large"
            label={t('settings.cancel')}
            onClick={handleCancel}
          />
          <Button
            className={styles.saveButton}
            variant="primary"
            size="large"
            label={t('settings.save')}
            onClick={handleSave}
            disabled={isLoading}
            data-testid="save-button"
          />
        </div>
      </div>
    </Drawer>
  );
};
